import { useEffect, useState } from "react";
import axios from "axios";
import { ReferralCard } from "./Referrals";
import { AlertTriangle, Check, Crown, LogOut, ShieldCheck, Sparkles } from "lucide-react";
import { SchoolInvoices } from "./Invoices";
import { BillingTerms, CycleToggle, OfferCountdown, PriceTag, quoteOf } from "./PriceTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((c) => { const t = localStorage.getItem("feemitra_token"); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });
const inr = (n = 0) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

function loadCheckout() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false); document.body.appendChild(s);
  });
}

async function payForPlan(plan, cycle, expected) {
  const order = (await api.post("/subscription/order", { plan, cycle, expected_amount: expected })).data;
  if (!(await loadCheckout())) throw new Error("Could not load Razorpay");
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key, amount: order.amount, currency: order.currency, name: order.name, description: order.description,
      order_id: order.id, prefill: order.prefill, theme: { color: "#1E3A8A" },
      handler: (resp) => api.post("/subscription/verify", resp).then(r => { if (r.data.payment_status !== "paid") reject(new Error("Payment received — confirming with Razorpay. Your plan will activate automatically in a minute.")); else resolve(r.data); }).catch(reject),
      modal: { ondismiss: () => reject(new Error("dismissed")) },
    });
    rzp.on("payment.failed", () => reject(new Error("Payment failed")));
    rzp.open();
  });
}

function StatusCard({ sub }) {
  if (sub.managed) return <div className="sub-status" data-testid="subscription-status"><ShieldCheck size={20} /><div><strong>Plan: {sub.plan}</strong><p className="muted">Your plan is currently managed by the FeeMitra team. Pay online below to switch to self-serve billing.</p></div></div>;
  const label = sub.expired ? "Expired" : sub.status === "trial" ? "Free trial" : "Active";
  return <div className={`sub-status ${sub.expired ? "expired" : sub.status}`} data-testid="subscription-status">
    {sub.expired ? <AlertTriangle size={20} /> : <Crown size={20} />}
    <div><strong>{label} · {sub.plans.find(p => p.key === sub.plan)?.name || sub.plan} plan</strong>
      <p className="muted" data-testid="subscription-expiry">{sub.expired ? `Expired on ${fmtDate(sub.expires_at)}` : `${sub.days_left} day(s) left · valid till ${fmtDate(sub.expires_at)}`}{sub.cycle ? ` · billed ${sub.cycle}` : ""}</p></div>
  </div>;
}

function PlanPicker({ sub, onPaid, onRefresh }) {
  const [cycle, setCycle] = useState(sub.offer?.yearly_enabled === false ? "monthly" : "yearly"); const [busy, setBusy] = useState(""); const [err, setErr] = useState("");
  const total = (q) => Math.round(q.payable * (1 + (sub.gst_rate || 0) / 100) * 100) / 100;
  const buy = async (p) => {
    setBusy(p.key); setErr("");
    try { onPaid(await payForPlan(p.key, cycle, total(quoteOf(p, cycle)))); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "PRICE_CHANGED") { setErr(d.message); onRefresh?.(); }
      else if (e.message !== "dismissed") setErr(typeof d === "string" ? d : d?.message || e.message || "Payment failed");
    }
    finally { setBusy(""); }
  };
  const r = sub.renewal;
  return <section className="section-block">
    <div className="section-title"><div><h2>{sub.status === "trial" || sub.expired ? "Choose your plan" : "Renew or upgrade"}</h2><p className="muted" data-testid="sub-price-note">{sub.gst_rate > 0 ? `Prices exclude ${sub.gst_rate}% GST · a GST invoice is created automatically.` : "No GST — you pay exactly the price shown. An invoice is created automatically."}</p></div>
      <CycleToggle cycle={cycle} setCycle={setCycle} offer={sub.offer} /></div>
    {r && <div className="info-box" data-testid="sub-renewal-price">Renewal price for your {sub.plans.find(p => p.key === r.plan)?.name} plan ({r.cycle}) today: <b>{inr(total(r))}</b>{r.discount_pct > 0 ? ` (Launch Offer ${r.discount_pct}% off standard ${inr(r.standard)})` : ""}{sub.last_paid ? ` · last paid ${inr(sub.last_paid.amount)}` : ""}. Your current plan stays as it is until you renew.</div>}
    <OfferCountdown offer={sub.offer} testid="sub-offer-countdown" />
    {sub.gateway === "test" && <div className="info-box" data-testid="sub-sandbox-note">TEST MODE — use Razorpay test cards/UPI; no real money is charged.</div>}
    {sub.gateway === "disabled" && <div className="error-box" data-testid="sub-gateway-disabled">Online plan payments are not enabled yet — Razorpay keys are not configured on FeeMitra. Please contact FeeMitra support.</div>}
    <div className="sub-plan-grid">{sub.plans.map(p => <div key={p.key} className={`sub-plan ${p.key === sub.plan ? "current" : ""}`} data-testid={`sub-plan-${p.key}`}>
      <strong>{p.name}</strong><small>{p.student_limit >= 100000 ? "Unlimited students" : `Up to ${p.student_limit.toLocaleString("en-IN")} students`}</small>
      <PriceTag p={p} cycle={cycle} testid={`sub-price-${p.key}`} />
      {sub.gst_rate > 0 && <small className="muted">+ {sub.gst_rate}% GST = {inr(total(quoteOf(p, cycle)))}</small>}
      <button className="primary-btn wide" disabled={!!busy || sub.gateway === "disabled"} onClick={() => buy(p)} data-testid={`buy-plan-${p.key}`}>{busy === p.key ? "Processing…" : p.key === sub.plan && !sub.expired && sub.status === "active" ? "Extend" : "Pay & activate"}</button>
      {sub.gateway === "disabled" && <small className="muted" data-testid={`buy-plan-${p.key}-disabled-note`}>Online payment not enabled yet</small>}
    </div>)}</div>
    {err && <div className="error-box" style={{ marginTop: 14 }} data-testid="subscription-error">{err}</div>}
    <BillingTerms offer={sub.offer} testid="sub-billing-terms" />
  </section>;
}

export function PlanBillingPage({ notify }) {
  const [sub, setSub] = useState(null);
  const load = () => api.get("/subscription").then(r => setSub(r.data));
  useEffect(() => { load(); }, []);
  if (!sub) return null;
  return <div className="plan-page" data-testid="plan-billing-page">
    <StatusCard sub={sub} />
    <PlanPicker sub={sub} onRefresh={load} onPaid={(s) => { setSub(s); notify?.("Payment successful — plan activated"); }} />
    {!sub.managed && <ReferralCard />}
    <SchoolInvoices />
  </div>;
}

export function TrialBanner({ onOpen }) {
  const [sub, setSub] = useState(null);
  useEffect(() => { api.get("/subscription").then(r => setSub(r.data)).catch(() => {}); }, []);
  if (!sub || sub.managed || sub.expired || sub.days_left > 7) return null;
  return <div className="trial-banner" data-testid="trial-banner"><Sparkles size={15} />
    <span>{sub.status === "trial" ? "Free trial" : "Your plan"} ends in <b>{sub.days_left} day(s)</b>.</span>
    <button className="text-btn" onClick={onOpen} data-testid="trial-banner-cta">Choose a plan</button></div>;
}

export function PlanExpiredScreen({ user, onLogout, onRenewed }) {
  const isAdmin = user.role === "admin";
  const [sub, setSub] = useState(null);
  useEffect(() => { if (isAdmin) api.get("/subscription").then(r => setSub(r.data)); }, [isAdmin]);
  return <div className="expired-shell" data-testid="plan-expired-screen">
    <div className="expired-card">
      <div className="expired-head"><AlertTriangle size={22} /><div><h2>FeeMitra plan expired</h2>
        <p className="muted">{isAdmin ? "Renew your plan to unlock your school's data. Nothing has been deleted." : "Your school's FeeMitra plan has expired. Please contact the school office."}</p></div>
        <button className="secondary-btn" onClick={onLogout} data-testid="expired-logout"><LogOut size={14} /> Sign out</button></div>
      {sub && <PlanPicker sub={sub} onPaid={(s) => { if (!s.expired) onRenewed(); }} />}
      {isAdmin && <p className="muted" style={{ textAlign: "center" }}><Check size={12} /> Instant activation after payment</p>}
    </div>
  </div>;
}
